import { useState } from 'react';
import { fmtDateTime, type EnrolmentCodeDTO } from '@em/shared';
import { api, qk, queryClient, useDevices, useSettings, useSites } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { titleCase } from '../../components/format';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, ErrorAlert, Field, Input, QueryState, Select } from '../../components/ui';

export function KiosksSettings() {
  const { can } = useAuth();
  const q = useDevices('kiosk');
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const toast = useToast();
  const [issuing, setIssuing] = useState(false);

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast.success(msg);
    } catch (e) {
      toast.error(e);
    }
  };
  const stale = (iso: string | null) => !iso || Date.now() - Date.parse(iso) > 15 * 60_000;

  return (
    <Card
      title="Factory kiosks"
      tight
      actions={
        can('owner') && (
          <Button variant="primary" size="sm" onClick={() => setIssuing(true)}>
            Enrol a kiosk
          </Button>
        )
      }
    >
      <QueryState q={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title="No kiosks enrolled" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kiosk</th>
                    <th>Site</th>
                    <th>Last sync</th>
                    <th className="num">Battery</th>
                    <th className="num">Queued punches</th>
                    <th className="num">Clock skew</th>
                    <th>App</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.map((d) => (
                    <tr key={d.id}>
                      <td className="strong">
                        {d.name}
                        <div className="small muted">{d.platform ?? ''}</div>
                      </td>
                      <td>{d.siteName ?? '–'}</td>
                      <td className="nowrap">
                        {d.lastSeenAt ? fmtDateTime(d.lastSeenAt, tz) : 'Never'} {d.status === 'active' && stale(d.lastSeenAt) && <Badge tone="red">Offline</Badge>}
                      </td>
                      <td className={`num ${d.battery != null && d.battery < 0.2 ? 'red strong' : ''}`}>{d.battery != null ? `${Math.round(d.battery * 100)}%` : '–'}</td>
                      <td className={`num ${d.queueDepth ? 'amber strong' : ''}`}>{d.queueDepth ?? '–'}</td>
                      <td className={`num ${d.clockSkewSeconds != null && Math.abs(d.clockSkewSeconds) > 120 ? 'red' : ''}`}>{d.clockSkewSeconds != null ? `${d.clockSkewSeconds} s` : '–'}</td>
                      <td>{d.appVersion ?? '–'}</td>
                      <td>
                        <Badge tone={d.status === 'active' ? 'green' : d.status === 'pending' ? 'amber' : 'grey'}>{titleCase(d.status)}</Badge>
                      </td>
                      <td className="actions">
                        {d.status === 'pending' && (
                          <Button size="sm" variant="primary" onClick={() => act(() => api.approveDevice(d.id), 'Kiosk approved')}>
                            Approve
                          </Button>
                        )}
                        {d.status !== 'revoked' && (
                          <Button size="sm" variant="danger" onClick={() => act(() => api.revokeDevice(d.id), 'Kiosk revoked')}>
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryState>
      {issuing && <KioskCodeDialog onClose={() => setIssuing(false)} />}
    </Card>
  );
}

function KioskCodeDialog({ onClose }: { onClose: () => void }) {
  const sites = useSites();
  const [siteId, setSiteId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState<EnrolmentCodeDTO | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const issue = async () => {
    setBusy(true);
    setError(null);
    try {
      setCode(await api.kioskEnrolmentCode({ siteId, name: name.trim() }));
      void queryClient.invalidateQueries({ queryKey: qk.devices('kiosk') });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  if (code)
    return (
      <Modal title="Kiosk enrolment code" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
        <div className="stack">
          <p>
            Enter this code on the tablet for <strong>{name}</strong>. It works once{code.expiresAt ? ` and expires ${fmtDateTime(code.expiresAt, 'Australia/Sydney')}` : ' and does not expire'}.
          </p>
          <div className="big-code">{code.code}</div>
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            <li>On the tablet, open <strong>{location.origin}/app/?mode=kiosk</strong> and add it to the home screen (iPad: Share, Add to Home Screen; Android: menu, Install app).</li>
            <li>Open it from the home-screen icon (it is called Kiosk) and enter the code. Entering it in the browser instead uses the code up.</li>
            <li>Lock the tablet in Guided Access (iPad) or app pinning (Android).</li>
          </ol>
          <Alert tone="amber">The code is shown only now.</Alert>
        </div>
      </Modal>
    );
  return (
    <Modal
      title="Enrol a kiosk"
      onClose={onClose}
      onSubmit={issue}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!siteId || !name.trim()}>
            Issue code
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <Field label="Site" required>
          <Select value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
            <option value="">Choose a site</option>
            {sites.data
              ?.filter((s) => s.active)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Kiosk name" hint="e.g. Factory entrance">
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
      </div>
    </Modal>
  );
}
